import { create } from 'zustand';
import { SelectedData, WorkDetailSimpleData, WorkSimpleForImportData, WorkSimpleWithDetails } from '../dto/EntityRepository';
import { IMPORT_STATION_SELECTED_MAX_COUNT } from '../constants/limits';

export interface ImportStationSelectedData extends SelectedData {
  formationSlotIndex?: number;
  sourceWork?: WorkSimpleWithDetails;
  sourceDetail?: WorkDetailSimpleData;
}

interface ImportStationWorkState {
  selectedDataList: ImportStationSelectedData[];
  setSelectedDataList: (list: ImportStationSelectedData[]) => void;
  addSelectedData: (data: ImportStationSelectedData) => void;
  removeSelectedData: (data: SelectedData) => void;
  clearSelectedDataList: () => void;
  removedCarouselItemIds: string[];
  addRemovedCarouselItemId: (id: string) => void;
  formationReplacementSlotIndexes: number[];
  addFormationReplacementSlotIndex: (slotIndex: number) => void;
  simpleList: WorkSimpleWithDetails[];
  setSimpleList: (list: WorkSimpleForImportData[]) => void;
  setDetailListForWork: (workId: string, detailList: WorkDetailSimpleData[]) => void;
  clearSimpleList: () => void;
}

const isSameSelection = (a: SelectedData, b: SelectedData) =>
  a.workId === b.workId && a.detailId === b.detailId;

export const useImportStationWorkStore = create<ImportStationWorkState>((set) => ({
  selectedDataList: [],
  setSelectedDataList: (list) => set({ selectedDataList: list }),
  addSelectedData: (data) =>
    set((state) => {
      if (state.selectedDataList.some((s) => isSameSelection(s, data))) return state;
      if (state.selectedDataList.length >= IMPORT_STATION_SELECTED_MAX_COUNT) return state;
      return { selectedDataList: [...state.selectedDataList, data] };
    }),
  removeSelectedData: (data) =>
    set((state) => ({
      selectedDataList: state.selectedDataList.filter((s) => !isSameSelection(s, data)),
    })),
  clearSelectedDataList: () => set({
    selectedDataList: [],
    removedCarouselItemIds: [],
    formationReplacementSlotIndexes: [],
  }),
  removedCarouselItemIds: [],
  addRemovedCarouselItemId: (id) =>
    set((state) => (
      state.removedCarouselItemIds.includes(id)
        ? state
        : { removedCarouselItemIds: [...state.removedCarouselItemIds, id] }
    )),
  formationReplacementSlotIndexes: [],
  addFormationReplacementSlotIndex: (slotIndex) =>
    set((state) => (
      state.formationReplacementSlotIndexes.includes(slotIndex)
        ? state
        : {
            formationReplacementSlotIndexes: [
              ...state.formationReplacementSlotIndexes,
              slotIndex,
            ],
          }
    )),
  simpleList: [],
  setSimpleList: (list) =>
    set({
      simpleList: list.map((work) => ({ ...work, detailList: [] as WorkDetailSimpleData[] })),
    }),
  setDetailListForWork: (workId, detailList) =>
    set((state) => ({
      simpleList: state.simpleList.map((work) =>
        work.id === workId ? { ...work, detailList } : work
      ),
    })),
  clearSimpleList: () =>
    set({
      selectedDataList: [],
      removedCarouselItemIds: [],
      formationReplacementSlotIndexes: [],
      simpleList: [],
    }),
}));
